'use client';

import {
    FILTER_REMOVE,
    FILTER_VAL,
    TRACK_TYPE_MULTI_ATTR,
} from '../Analytics/constants';
import { FacetNormalized } from '../SearchResultsFacets/types';
import {
    getFacetTagLabel,
    getFilterType,
    getQueryParamValue,
    getSelectedFacetsFromUrl,
} from '../SearchResultsFacets/utils';

import { useEffect, useState } from 'react';

import { SEARCH_SERVICE_PROVIDERS } from '@/constants/grs';
import { getKeyValue } from '@/services/content/data';
import { isSearchResultGRS } from '@/services/search/services/Grs';
import { isSearchResultLW } from '@/services/search/services/LucidWorks';
import {
    FACET_OPTION_SELECTED_FILTER,
    SELECTED_FACETS,
} from '@/src/components/SearchResultsFacets/constants';
import useRefinement from '@/src/hooks/useRefinement';
import { useSearchContext } from '@/src/services/search/SearchProvider';
import type { GRSFacetConfigProperties } from '@/types/contentStack';
import { Box } from '@mui/material';
import {
    ColorBlue40,
    SpaceXs,
    SpaceXxs,
} from '@nxt-costco-com/forge-design-tokens';
import {
    type ContentStackEntryDataProps,
    type LocaleProps,
    useQueryParams,
} from '@nxt-costco-com/forge-digital-components';
import { Analytics } from '@nxt-costco-com/forge-digital-components';
import { XThin } from '@nxt-costco-com/forge-icons';

import { StyledButton } from './styles';

export function SelectedFilters({
    facetConfig,
    grsFacetConfig,
    locale,
}: {
    facetConfig: ContentStackEntryDataProps | undefined;
    grsFacetConfig:
        | ContentStackEntryDataProps<GRSFacetConfigProperties>
        | undefined;
    locale: LocaleProps;
}) {
    const { searchResult, searchService } = useSearchContext();
    const [selectedFacets, setSelectedFacets] = useState<FacetNormalized[]>([]);
    const { queryParams: searchParams } = useQueryParams();
    const { handleDoRefine } = useRefinement({ searchService, facetConfig });

    useEffect(() => {
        if (
            searchResult &&
            facetConfig &&
            ((searchService === SEARCH_SERVICE_PROVIDERS.LUCIDWORKS &&
                isSearchResultLW(searchResult)) ||
                (searchService === SEARCH_SERVICE_PROVIDERS.GRS &&
                    isSearchResultGRS(searchResult) &&
                    grsFacetConfig))
        ) {
            const selectFacets = getSelectedFacetsFromUrl({
                searchParams,
                searchResult,
                grsFacetConfig,
                locale,
                searchService,
                facetConfig,
            });

            setSelectedFacets(selectFacets ?? []);
        }
    }, [
        facetConfig,
        grsFacetConfig,
        locale,
        searchParams,
        searchResult,
        searchService,
    ]);

    if (
        !facetConfig ||
        (searchService === SEARCH_SERVICE_PROVIDERS.GRS && !grsFacetConfig)
    ) {
        return null;
    }

    const removeFilterText = getKeyValue('a11y.removefiltertext', facetConfig);

    return (
        <Box
            sx={{
                display: 'flex',
                margin: '16px 0 0',
                '@media (max-width: 768px)': {
                    margin: '12px 0 0 0',
                },
                flexWrap: 'wrap',
            }}
            data-testid={SELECTED_FACETS}
        >
            <>
                {selectedFacets.map(facet => {
                    return facet.buckets.map((bucket, index) => {
                        const label = getFacetTagLabel({
                            facet,
                            bucket,
                            facetConfig,
                            grsFacetConfig:
                                grsFacetConfig as ContentStackEntryDataProps<GRSFacetConfigProperties>,
                            locale,
                        });

                        const queryParamKey =
                            facet.queryParamKey ?? facet.facetKey;
                        const queryParamValue = getQueryParamValue(
                            facet,
                            bucket,
                            searchService,
                            facetConfig,
                        );
                        const filterType = getFilterType(queryParamKey);

                        const refinement = `${queryParamKey}-${queryParamValue}`;
                        return (
                            <Analytics
                                analyticData={{
                                    trackedData: [
                                        {
                                            key: FILTER_REMOVE,
                                            value: filterType,
                                        },
                                        {
                                            key: FILTER_VAL,
                                            value: label,
                                        },
                                    ],
                                    trackType: TRACK_TYPE_MULTI_ATTR,
                                }}
                                key={refinement + '-analytics'}
                            >
                                <StyledButton
                                    key={`${refinement}-${index}-analytics`}
                                    onClick={() =>
                                        handleDoRefine(facet, bucket, false)
                                    }
                                    aria-label={`${label}, ${removeFilterText}`}
                                    uniqueId={`${FACET_OPTION_SELECTED_FILTER}_${label}`}
                                    sx={{
                                        marginBottom: '14px',
                                        padding: `${SpaceXxs} ${SpaceXs}`,
                                    }}
                                >
                                    {label}
                                    <XThin
                                        style={{
                                            color: ColorBlue40,
                                            height: '14px',
                                            paddingLeft: '12px',
                                        }}
                                    />
                                </StyledButton>
                            </Analytics>
                        );
                    });
                })}
            </>
        </Box>
    );
}
